import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import {
  companyCreateInputSchema,
  companySchema,
  type Company,
  type CompanyCreateInput,
} from "@/schemas";

const companiesListSchema = z.array(companySchema);

export const companyKeys = {
  all: ["companies"] as const,
  detail: (id: number) => ["companies", id] as const,
};

export function useCompanies() {
  return useQuery({
    queryKey: companyKeys.all,
    queryFn: async (): Promise<Company[]> => {
      const data = await api.get<unknown>("/companies");
      return companiesListSchema.parse(data);
    },
  });
}

export function useCompany(companyId: number | undefined) {
  return useQuery({
    queryKey: companyId ? companyKeys.detail(companyId) : ["companies", "none"],
    enabled: !!companyId,
    queryFn: async (): Promise<Company> => {
      const data = await api.get<unknown>(`/companies/${companyId}`);
      return companySchema.parse(data);
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompanyCreateInput) => {
      const parsed = companyCreateInputSchema.parse(input);
      const data = await api.post<unknown>("/companies", parsed);
      return companySchema.parse(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: companyKeys.all }),
  });
}
